import { request } from '@/api/request'

/** Superadmin catalog: products (+ components, record-key patterns), clients, environments, tenants. */

export type LifecycleStatus = 'active' | 'retired'
export type ClientStatus = 'active' | 'inactive' | 'suspended'
export type EnvKind = 'production' | 'validation' | 'test' | 'training' | 'sandbox'
export type KeyNormalizer = 'none' | 'upper' | 'strip_spaces_upper'

export interface CatalogProductRow {
  id: string
  key: string
  name: string
  description: string | null
  slaTargetPct: number
  active: boolean
  createdAt: string
  _count: { tickets: number; environments: number; components: number; keyPatterns: number }
}

export interface CatalogComponent {
  id: string
  productId: string
  key: string
  name: string
  active: boolean
  _count?: { tickets: number }
}

export interface KeyPattern {
  id: string
  productId: string
  type: string
  regex: string
  sample: string | null
  normalizer: string
  urlTemplate: string | null
}

export interface CatalogProduct extends Omit<CatalogProductRow, '_count'> {
  components: CatalogComponent[]
  keyPatterns: KeyPattern[]
  _count: { tickets: number; environments: number }
}

export interface ProductInput {
  key?: string
  name?: string
  description?: string | null
  slaTargetPct?: number
  active?: boolean
}

export interface KeyPatternInput {
  type: string
  regex: string
  sample: string
  normalizer: KeyNormalizer
  urlTemplate: string | null
}

export interface ClientRow {
  id: string
  name: string
  slug: string
  status: ClientStatus
  timeZone: string
  emailDomains: string[]
  createdAt: string
  updatedAt: string
  _count: { tenants: number; environments: number; tickets: number; contacts: number }
  /** The list's slim environments: which products the client runs. */
  environments: ClientRowEnvironment[]
}

export interface ClientRowEnvironment {
  id: string
  name: string
  kind: EnvKind
  status: LifecycleStatus
  product: { id: string; key: string; name: string }
}

export interface ClientEnvironment {
  id: string
  customerId: string
  productId: string
  name: string
  kind: EnvKind
  isValidated: boolean
  baseUrl: string | null
  status: LifecycleStatus
  product: { id: string; key: string; name: string; active: boolean }
  _count: { tickets: number; tenantLinks: number }
}

export interface ClientDetail extends Omit<ClientRow, 'environments'> {
  environments: ClientEnvironment[]
}

export interface ClientInput {
  name?: string
  slug?: string
  timeZone?: string
  emailDomains?: string[]
  reason?: string
}

export interface EnvironmentInput {
  productId?: string
  name?: string
  kind?: EnvKind
  isValidated?: boolean
  baseUrl?: string | null
  status?: LifecycleStatus
}

export interface TenantLink {
  environmentId: string
  externalTenantId: string | null
}

export interface Tenant {
  id: string
  customerId: string
  name: string
  code: string
  timeZone: string
  location: string | null
  status: LifecycleStatus
  environments: (TenantLink & {
    tenantId: string
    environment: { id: string; name: string; kind: EnvKind; status: LifecycleStatus; product: { id: string; key: string; name: string } }
  })[]
  _count: { tickets: number; contacts: number; users: number }
}

export interface TenantInput {
  name?: string
  code?: string
  timeZone?: string
  location?: string | null
  status?: LifecycleStatus
  environments?: TenantLink[]
}

export type IntegrationScope = 'tickets.read' | 'tickets.write' | 'webhooks.manage'
export const INTEGRATION_SCOPES: readonly IntegrationScope[] = ['tickets.read', 'tickets.write', 'webhooks.manage']

/** An environment's API key (an Integration API client) as the API access tab shows it. */
export interface ApiKey {
  id: string
  name: string
  clientId: string
  scopes: string[]
  status: 'active' | 'disabled'
  role: 'active' | 'replacement' | 'disabled'
  replacesId: string | null
  createdAt: string
  lastUsedAt: string | null
  webhooks: { active: number; failing: number; lastDelivery: { status: string; at: string } | null }
}

export interface ApiAccessTenant {
  id: string
  name: string
  code: string
  externalTenantId: string | null
  lastSeenAt: string | null
  openTickets: number
}

export interface ApiAccessEnvironment {
  id: string
  name: string
  kind: EnvKind
  product: { key: string; name: string }
  keys: ApiKey[]
  tenants: ApiAccessTenant[]
}

/** GET /admin/integration-clients/overview?customerId= */
export interface ApiAccessOverview {
  customer: { id: string; name: string; hasContacts: boolean }
  apiBaseUrl: string
  tokenUrl: string
  environments: ApiAccessEnvironment[]
}

export interface CreateKeyInput {
  customerEnvironmentId: string
  name: string
  scopes: IntegrationScope[]
  defaultContact?: { name: string; email: string }
}

/** A just-issued key: the only time the secret is readable. */
export interface IssuedKey {
  client: { id: string; name: string; clientId: string; scopes: string[] }
  clientSecret: string | null
  apiKeyValue: string
  tokenUrl: string
  replaces?: { id: string; clientId: string }
}

export interface KeyDelivery {
  id: string
  eventId: string
  eventType: string
  status: string
  attempts: number
  lastError: string | null
  createdAt: string
  deliveredAt: string | null
  subscription: { url: string }
}

const enc = encodeURIComponent
const json = (method: string, body: unknown) => ({ method, body: JSON.stringify(body) })

export const catalogApi = {
  products: (): Promise<CatalogProductRow[]> => request('/admin/products'),
  product: (id: string): Promise<CatalogProduct> => request(`/admin/products/${enc(id)}`),
  createProduct: (input: ProductInput): Promise<CatalogProduct> => request('/admin/products', json('POST', input)),
  updateProduct: (id: string, input: ProductInput): Promise<CatalogProduct> => request(`/admin/products/${enc(id)}`, json('PATCH', input)),
  addComponent: (productId: string, input: { key: string; name: string }): Promise<CatalogComponent> =>
    request(`/admin/products/${enc(productId)}/components`, json('POST', input)),
  updateComponent: (productId: string, id: string, input: { name?: string; active?: boolean }): Promise<CatalogComponent> =>
    request(`/admin/products/${enc(productId)}/components/${enc(id)}`, json('PATCH', input)),
  removeComponent: (productId: string, id: string): Promise<void> =>
    request(`/admin/products/${enc(productId)}/components/${enc(id)}`, { method: 'DELETE' }),
  addKeyPattern: (productId: string, input: KeyPatternInput): Promise<KeyPattern> =>
    request(`/admin/products/${enc(productId)}/key-patterns`, json('POST', input)),
  updateKeyPattern: (productId: string, id: string, input: Partial<KeyPatternInput>): Promise<KeyPattern> =>
    request(`/admin/products/${enc(productId)}/key-patterns/${enc(id)}`, json('PATCH', input)),
  removeKeyPattern: (productId: string, id: string): Promise<void> =>
    request(`/admin/products/${enc(productId)}/key-patterns/${enc(id)}`, { method: 'DELETE' }),

  clients: (q?: string): Promise<ClientRow[]> => request(`/admin/customers${q ? `?q=${enc(q)}` : ''}`),
  client: (id: string): Promise<ClientDetail> => request(`/admin/customers/${enc(id)}`),
  createClient: (input: ClientInput): Promise<ClientDetail> => request('/admin/customers', json('POST', input)),
  updateClient: (id: string, input: ClientInput): Promise<ClientDetail> => request(`/admin/customers/${enc(id)}`, json('PATCH', input)),
  setClientStatus: (id: string, action: 'suspend' | 'reactivate', reason?: string): Promise<ClientDetail> =>
    request(`/admin/customers/${enc(id)}/${action}`, json('POST', reason ? { reason } : {})),
  addEnvironment: (clientId: string, input: EnvironmentInput): Promise<ClientEnvironment> =>
    request(`/admin/customers/${enc(clientId)}/environments`, json('POST', input)),
  updateEnvironment: (clientId: string, id: string, input: EnvironmentInput): Promise<ClientEnvironment> =>
    request(`/admin/customers/${enc(clientId)}/environments/${enc(id)}`, json('PATCH', input)),
  removeEnvironment: (clientId: string, id: string): Promise<void> =>
    request(`/admin/customers/${enc(clientId)}/environments/${enc(id)}`, { method: 'DELETE' }),
  tenants: (clientId: string): Promise<Tenant[]> => request(`/admin/customers/${enc(clientId)}/tenants`),
  createTenant: (clientId: string, input: TenantInput): Promise<Tenant> =>
    request(`/admin/customers/${enc(clientId)}/tenants`, json('POST', input)),
  updateTenant: (clientId: string, id: string, input: TenantInput): Promise<Tenant> =>
    request(`/admin/customers/${enc(clientId)}/tenants/${enc(id)}`, json('PATCH', input)),
  removeTenant: (clientId: string, id: string): Promise<void> =>
    request(`/admin/customers/${enc(clientId)}/tenants/${enc(id)}`, { method: 'DELETE' }),

  apiAccess: (clientId: string): Promise<ApiAccessOverview> => request(`/admin/integration-clients/overview?customerId=${enc(clientId)}`),
  createKey: (input: CreateKeyInput): Promise<IssuedKey> => request('/admin/integration-clients', json('POST', input)),
  rotateKey: (id: string): Promise<IssuedKey> => request(`/admin/integration-clients/${enc(id)}/rotate`, { method: 'POST' }),
  setKeyStatus: (id: string, action: 'disable' | 'enable'): Promise<unknown> =>
    request(`/admin/integration-clients/${enc(id)}/${action}`, { method: 'POST' }),
  keyDeliveries: (id: string): Promise<KeyDelivery[]> => request(`/admin/integration-clients/${enc(id)}/deliveries`),
}
